// ⌘K: jump anywhere, start a role-play, open a lesson, find a saved word, or ask
// Pashmak about any phrase — the keyboard-first way around the app.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useContent, allLessons } from "../lib/content";
import { useLearn } from "../lib/state";
import { Modal } from "./ui";
import { NAV } from "./Layout";
import { Fa } from "./Phrase";
import { IChat, IPhone, IPath, ICards, ISettings, ICrown, IArrowRight } from "./icons";

interface Item { id: string; label: string; hint?: string; fa?: string; icon: (p: any) => React.ReactNode; run: () => void }

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const nav = useNavigate();
  const content = useContent();
  const words = useLearn((s) => s.words);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setSel(0);
      setTimeout(() => input.current?.focus(), 30);
    }
  }, [open]);

  const go = (to: string) => () => {
    onClose();
    nav(to);
  };

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [
      ...NAV.map((n) => ({ id: `nav${n.to}`, label: n.label, hint: n.key ? `g ${n.key}` : undefined, icon: n.icon, run: go(n.to) })),
      { id: "review", label: "Review due words", icon: ICards, run: go("/words/review") },
      { id: "pro", label: "Pashmak Pro", icon: ICrown, run: go("/pro") },
      { id: "settings", label: "Settings", icon: ISettings, run: go("/settings") },
    ];
    if (content) {
      for (const s of content.scenarios) out.push({ id: `sc-${s.id}`, label: `Role-play: ${s.title}`, fa: s.titleFa, icon: IPhone, run: go(`/talk/${s.id}`) });
      for (const l of allLessons(content)) out.push({ id: `l-${l.id}`, label: `Lesson: ${l.title}`, fa: l.titleFa, icon: IPath, run: go(`/learn/${l.id}`) });
    }
    for (const w of Object.values(words)) if (!w.deleted) out.push({ id: `w-${w.fa}`, label: `${w.en}`, hint: w.finglish, fa: w.fa, icon: ICards, run: go(`/words?q=${encodeURIComponent(w.fa)}`) });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, words]);

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = t ? items.filter((i) => `${i.label} ${i.hint || ""} ${i.fa || ""}`.toLowerCase().includes(t)) : items.slice(0, 12);
    const ask: Item[] = t
      ? [{ id: "ask", label: `Ask Pashmak: “${q.trim()}”`, hint: "Chat", icon: IChat, run: go(`/chat?q=${encodeURIComponent(q.trim())}`) }]
      : [];
    return [...list.slice(0, 30), ...ask];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, items]);

  useEffect(() => setSel(0), [q]);

  return (
    <Modal open={open} onClose={onClose} label="Search" wide sheet={false}>
      <div className="palette" style={{ margin: -26 }}>
        <input
          ref={input}
          className="input"
          placeholder="Search lessons, role-plays, your words… or ask Pashmak anything"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search"
          role="combobox"
          aria-expanded="true"
          aria-controls="palette-list"
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") (e.preventDefault(), setSel((s) => Math.min(s + 1, results.length - 1)));
            if (e.key === "ArrowUp") (e.preventDefault(), setSel((s) => Math.max(s - 1, 0)));
            if (e.key === "Enter") results[sel]?.run();
          }}
        />
        <ul id="palette-list" role="listbox">
          {results.map((r, i) => (
            <li key={r.id}>
              <button role="option" aria-selected={i === sel} onMouseEnter={() => setSel(i)} onClick={r.run}>
                <r.icon width={18} height={18} />
                <span style={{ flex: 1, minWidth: 0 }}>{r.label}</span>
                {r.fa && <Fa className="small">{r.fa}</Fa>}
                {r.hint && <kbd>{r.hint}</kbd>}
                {i === sel && <IArrowRight width={16} height={16} />}
              </button>
            </li>
          ))}
          {!results.length && <li className="sub" style={{ padding: 14 }}>Nothing found.</li>}
        </ul>
      </div>
    </Modal>
  );
}
